import { useState, useEffect, useMemo, useRef, type FormEvent } from 'react'
import { useInstructorExternalSchools, type ExternalSchool, type SchoolFields } from '@/hooks/useInstructorExternalSchools'
import { useInstructorSchoolMemberships } from '@/hooks/useInstructorSchoolMemberships'
import { useContractorAffiliationRequests, type SchoolSearchResult } from '@/hooks/useContractorAffiliationRequests'
import { useStudents } from '@/hooks/useStudents'
import { Icon, ICONS } from '@/components/ui/Icon'
import { IconButton } from '@/components/ui/IconButton'
import { Badge, type BadgeTone } from '@/components/ui/Badge'

type RowType = 'employee' | 'contractor' | 'private'
type FilterKey = 'all' | 'registered' | 'private' | 'archived'
type SortKey = 'name' | 'rate'

interface UnifiedRow {
  key: string
  schoolId: string
  name: string
  type: RowType
  archived: boolean
  address: string | null
  rate: number | null
  rateCurrency: string
  email: string | null
  phone: string | null
  // Only set for private-label rows -- used to render inline edit/archive actions.
  external?: ExternalSchool
}

function formatRate(rate: number | null, currency: string): string {
  if (rate === null) return '—'
  return `$${rate.toFixed(2)} ${currency}/hr`
}

const TYPE_LABEL: Record<RowType, string> = { employee: 'Employee', contractor: 'Contractor', private: 'Private label' }
const TYPE_STYLE: Record<RowType, { color: string; bg: string }> = {
  employee:   { color: '#0F7C6C', bg: 'rgba(15,124,108,0.1)' },
  contractor: { color: '#1B2A4A', bg: 'rgba(27,42,74,0.07)' },
  private:    { color: 'rgba(27,42,74,0.6)', bg: 'rgba(27,42,74,0.05)' },
}
const TYPE_TONE: Record<RowType, BadgeTone> = { employee: 'success', contractor: 'neutral', private: 'neutral' }

interface FormState {
  name: string
  address: string
  contactName: string
  contactEmail: string
  contactPhone: string
  hourlyRate: string
}
const EMPTY_FORM: FormState = { name: '', address: '', contactName: '', contactEmail: '', contactPhone: '', hourlyRate: '' }

function toFields(f: FormState): SchoolFields {
  const rate = f.hourlyRate.trim()
  return {
    name: f.name.trim(),
    address: f.address.trim() || null,
    contactName: f.contactName.trim() || null,
    contactEmail: f.contactEmail.trim() || null,
    contactPhone: f.contactPhone.trim() || null,
    hourlyRate: rate ? Number(rate) : null,
  }
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
}

function SchoolForm({
  initial, submitting, submitLabel, onSubmit, onCancel,
}: {
  initial: FormState
  submitting: boolean
  submitLabel: string
  onSubmit: (fields: SchoolFields) => Promise<{ ok: boolean; error?: string }>
  onCancel?: () => void
}) {
  const [form, setForm] = useState<FormState>(initial)
  const [formError, setFormError] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setFormError('')
    if (!form.name.trim()) { setFormError('School name is required.'); return }
    if (form.hourlyRate.trim()) {
      const n = Number(form.hourlyRate)
      if (!Number.isFinite(n) || n <= 0) { setFormError('Hourly rate must be a positive number, or left blank.'); return }
    }
    const result = await onSubmit(toFields(form))
    if (!result.ok) { setFormError(result.error ?? 'Could not save this school.'); return }
    setForm(EMPTY_FORM)
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className='fg2' style={{ marginBottom: 16 }}>
        <div>
          <div style={fieldLabelStyle}><Icon path={ICONS.building} size={13} /> School name</div>
          <input
            type='text' value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder='e.g. Maple Driving Academy' className='field-input' disabled={submitting} required
          />
        </div>
        <div>
          <div style={fieldLabelStyle}><Icon path={ICONS.dollar} size={13} /> Hourly rate (CAD, optional)</div>
          <input
            type='number' min='0.01' step='0.01' value={form.hourlyRate}
            onChange={e => setForm(f => ({ ...f, hourlyRate: e.target.value }))}
            placeholder='e.g. 45.00' className='field-input' disabled={submitting}
          />
        </div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={fieldLabelStyle}><Icon path={ICONS.pin} size={13} /> Address (optional)</div>
        <input
          type='text' value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
          placeholder='e.g. 123 Main St, Toronto, ON' className='field-input' disabled={submitting}
        />
      </div>
      <div className='fg2' style={{ marginBottom: 16 }}>
        <div>
          <div style={fieldLabelStyle}><Icon path={ICONS.users} size={13} /> Contact name (optional)</div>
          <input
            type='text' value={form.contactName} onChange={e => setForm(f => ({ ...f, contactName: e.target.value }))}
            className='field-input' disabled={submitting}
          />
        </div>
        <div>
          <div style={fieldLabelStyle}><Icon path={ICONS.mail} size={13} /> Contact email (optional)</div>
          <input
            type='email' value={form.contactEmail} onChange={e => setForm(f => ({ ...f, contactEmail: e.target.value }))}
            className='field-input' disabled={submitting}
          />
        </div>
      </div>
      <div style={{ marginBottom: 18, maxWidth: 260 }}>
        <div style={fieldLabelStyle}><Icon path={ICONS.phone} size={13} /> Contact phone (optional)</div>
        <input
          type='tel' value={form.contactPhone} onChange={e => setForm(f => ({ ...f, contactPhone: e.target.value }))}
          className='field-input' disabled={submitting}
        />
      </div>
      {formError && <div className='banner-error' role='alert' style={{ marginBottom: 14 }}>{formError}</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className='btn-teal' type='submit' disabled={submitting} style={{ width: 'auto', padding: '11px 22px' }}>
          {submitting ? 'Saving…' : submitLabel}
        </button>
        {onCancel && (
          <button
            type='button' onClick={onCancel}
            style={{ background: 'none', border: 'none', font: '500 13px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', cursor: 'pointer', padding: '11px 8px' }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}

function SchoolStudents({ schoolId, isExternal }: { schoolId: string; isExternal: boolean }) {
  const { students, loading } = useStudents()
  const attributed = students.filter(s => (isExternal ? s.externalSchoolId : s.schoolId) === schoolId)

  if (loading) return <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Loading students…</div>
  if (attributed.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>
        <Icon path={ICONS.inbox} size={15} /> No students attributed to this school yet.
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {attributed.map(s => (
        <div key={s.relationshipId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%', background: 'rgba(27,42,74,0.07)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            font: '600 10.5px Inter, sans-serif', color: '#1B2A4A',
          }}>
            {(s.fullName ?? s.email).charAt(0).toUpperCase()}
          </div>
          <div style={{ font: '400 13px Inter, sans-serif', color: '#1B2A4A' }}>{s.fullName ?? s.email}</div>
        </div>
      ))}
    </div>
  )
}

// minmax(0, Nfr), not plain Nfr -- a grid track's implicit min-width is "auto" (its
// content's min-content size), so a long nowrap value (the Pay/hr column) can force its
// track to grow past its fr share and squeeze/overlap the next column. minmax(0, …)
// removes that implicit floor so each column actually respects its declared share.
const COLS = 'minmax(0,1.6fr) minmax(0,0.7fr) minmax(0,1fr) minmax(0,0.8fr) minmax(0,1.6fr) minmax(0,1fr) auto'

function HeaderCell({ label, sortKey, activeSort, activeDir, onSort }: {
  label: string
  sortKey?: SortKey
  activeSort?: SortKey
  activeDir?: 'asc' | 'desc'
  onSort?: (key: SortKey) => void
}) {
  const isActive = sortKey && activeSort === sortKey
  if (!sortKey || !onSort) {
    return <span>{label}</span>
  }
  return (
    <button
      onClick={() => onSort(sortKey)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none',
        cursor: 'pointer', padding: 0, font: 'inherit', color: isActive ? '#0F7C6C' : 'inherit',
      }}
    >
      {label}
      <Icon path={isActive && activeDir === 'desc' ? ICONS.chevronUp : ICONS.sort} size={11} />
    </button>
  )
}

const AFFILIATION_STATUS_TONE: Record<string, BadgeTone> = { pending: 'warning', approved: 'success', rejected: 'danger' }
const AFFILIATION_STATUS_LABEL: Record<string, string> = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' }

const AFFILIATION_REQUEST_ERROR_MESSAGES: Record<string, string> = {
  not_instructor:     'Only instructor accounts can request a school affiliation.',
  school_not_found:   'That school could not be found.',
  already_affiliated: 'You are already affiliated with this school.',
  already_pending:    'You already have a pending request for this school.',
  internal_error:     'Something went wrong. Please try again.',
}

// Contractor self-service school affiliation (migration 078) — the only path left for a
// contractor to join a registered school's roster, since schools cannot invite
// contractors directly anymore (create_instructor_invitation()'s rejection).
function RequestToJoinPanel() {
  const { requests: affiliationRequests, searchSchools, requestAffiliation, requesting } = useContractorAffiliationRequests()
  const [schoolQuery,   setSchoolQuery]   = useState('')
  const [schoolResults, setSchoolResults] = useState<SchoolSearchResult[]>([])
  const [searching,     setSearching]     = useState(false)
  const [notice, setNotice] = useState('')
  const [requestError,  setRequestError]  = useState('')
  const debounceRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    window.clearTimeout(debounceRef.current)
    if (!schoolQuery.trim()) { setSchoolResults([]); return }
    debounceRef.current = window.setTimeout(async () => {
      setSearching(true)
      try {
        setSchoolResults(await searchSchools(schoolQuery.trim()))
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => window.clearTimeout(debounceRef.current)
  }, [schoolQuery, searchSchools])

  const handleRequest = async (schoolId: string) => {
    setNotice(''); setRequestError('')
    const result = await requestAffiliation(schoolId)
    if (!result.ok) {
      setRequestError(AFFILIATION_REQUEST_ERROR_MESSAGES[result.code ?? ''] ?? 'Could not send the request. Please try again.')
      return
    }
    setNotice('Request sent. The school will review it before anything connects.')
    setSchoolQuery(''); setSchoolResults([])
  }

  return (
    <div className='schools-form-enter' style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '24px 22px', marginTop: 18, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 4 }}>
        <Icon path={ICONS.briefcase} size={15} /> Request to join a registered school
      </div>
      <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginBottom: 14 }}>
        Search for a school already on DriveTrack and ask to be recognized as its
        contractor. Nothing connects until the school approves your request.
      </div>

      <input
        value={schoolQuery}
        onChange={e => setSchoolQuery(e.target.value)}
        placeholder='Search schools by name…'
        className='field-input'
      />

      {notice && (
        <div style={{
          marginTop: 12, padding: '10px 14px', borderRadius: 9,
          background: 'rgba(15,124,108,0.07)', border: '1px solid rgba(15,124,108,0.2)',
          font: '400 12.5px Inter, sans-serif', color: '#1B2A4A',
        }}>
          {notice}
        </div>
      )}
      {requestError && <div className='banner-error' style={{ marginTop: 12 }}>{requestError}</div>}

      {searching && (
        <div style={{ marginTop: 10, font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)' }}>Searching…</div>
      )}

      {!searching && schoolResults.length > 0 && (
        <div style={{ marginTop: 10, border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, overflow: 'hidden' }}>
          {schoolResults.map((s, i) => (
            <div key={s.id} style={{
              padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              borderBottom: i < schoolResults.length - 1 ? '1px solid rgba(27,42,74,0.06)' : 'none',
            }}>
              <div>
                <div style={{ font: '500 13px Inter, sans-serif', color: '#1B2A4A' }}>{s.name}</div>
                {s.city && <div style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}>{s.city}</div>}
              </div>
              <button
                onClick={() => handleRequest(s.id)}
                disabled={requesting}
                className='btn-teal'
                style={{ width: 'auto', padding: '6px 14px', font: '600 12px Inter, sans-serif', flexShrink: 0 }}
              >
                Request
              </button>
            </div>
          ))}
        </div>
      )}

      {affiliationRequests.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ font: '600 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
            My requests
          </div>
          {affiliationRequests.map(req => (
            <div key={req.id} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              padding: '8px 0', borderBottom: '1px solid rgba(27,42,74,0.05)',
            }}>
              <div style={{ font: '500 13px Inter, sans-serif', color: '#1B2A4A' }}>
                {req.schoolName ?? 'School'}
                {req.hourlyRate != null && (
                  <span style={{ font: '400 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)' }}> · ${req.hourlyRate.toFixed(2)}/hr</span>
                )}
              </div>
              <Badge label={AFFILIATION_STATUS_LABEL[req.status]} tone={AFFILIATION_STATUS_TONE[req.status]} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default function Schools() {
  const { schools, loading: loadingPrivate, error, creating, updating, createSchool, updateSchool, archiveSchool, reactivateSchool } = useInstructorExternalSchools()
  const { schools: registeredSchools, loading: loadingRegistered } = useInstructorSchoolMemberships()
  const [activePanel, setActivePanel] = useState<'none' | 'add' | 'request'>('none')
  const [filter, setFilter] = useState<FilterKey>('all')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const loading = loadingPrivate || loadingRegistered

  const rows: UnifiedRow[] = useMemo(() => {
    const registeredRows: UnifiedRow[] = registeredSchools.map(s => ({
      key: `reg-${s.schoolId}`,
      schoolId: s.schoolId,
      name: s.schoolName,
      type: s.relationshipType === 'contractor' ? 'contractor' : 'employee',
      archived: false,
      address: s.address,
      rate: s.hourlyRate,
      rateCurrency: 'CAD',
      email: s.email,
      phone: s.phone,
    }))
    const privateRows: UnifiedRow[] = schools.map(s => ({
      key: `priv-${s.id}`,
      schoolId: s.id,
      name: s.name,
      type: 'private',
      archived: s.status === 'archived',
      address: s.address,
      rate: s.hourlyRate,
      rateCurrency: s.rateCurrency,
      email: s.contactEmail,
      phone: s.contactPhone,
      external: s,
    }))
    return [...registeredRows, ...privateRows]
  }, [registeredSchools, schools])

  const counts = useMemo(() => ({
    all: rows.length,
    registered: rows.filter(r => r.type !== 'private').length,
    private: rows.filter(r => r.type === 'private' && !r.archived).length,
    archived: rows.filter(r => r.archived).length,
  }), [rows])

  const filtered = useMemo(() => {
    let list = rows
    if (filter === 'registered') list = list.filter(r => r.type !== 'private')
    else if (filter === 'private') list = list.filter(r => r.type === 'private' && !r.archived)
    else if (filter === 'archived') list = list.filter(r => r.archived)
    else list = list.filter(r => !r.archived)

    const dir = sortDir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      if (sortKey === 'rate') return ((a.rate ?? -1) - (b.rate ?? -1)) * dir
      return a.name.localeCompare(b.name) * dir
    })
  }, [rows, filter, sortKey, sortDir])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir('asc') }
  }

  const FILTERS: { key: FilterKey; label: string }[] = [
    { key: 'all', label: `All (${counts.all - counts.archived})` },
    { key: 'registered', label: `Registered (${counts.registered})` },
    { key: 'private', label: `Private (${counts.private})` },
    { key: 'archived', label: `Archived (${counts.archived})` },
  ]

  return (
    <div>
      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .schools-form-enter { animation: schoolsFormIn 0.18s ease-out; }
          @keyframes schoolsFormIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        }
        .schools-row:hover { background: rgba(27,42,74,0.015); }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14, marginBottom: 6 }}>
        <div>
          <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A' }}>Schools</div>
          <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 6, maxWidth: 560 }}>
            Every school you work with — registered DriveTrack schools and your own private labels — in one list.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => setActivePanel(p => (p === 'request' ? 'none' : 'request'))}
            style={{
              width: 'auto', padding: '10px 18px', display: 'inline-flex', alignItems: 'center', gap: 7,
              background: activePanel === 'request' ? 'rgba(27,42,74,0.06)' : '#fff',
              border: activePanel === 'request' ? 'none' : '1px solid rgba(27,42,74,0.15)',
              borderRadius: 8, color: '#1B2A4A', cursor: 'pointer', font: '600 13px Inter, sans-serif',
            }}
          >
            <Icon path={activePanel === 'request' ? ICONS.close : ICONS.briefcase} size={14} />
            {activePanel === 'request' ? 'Cancel' : 'Request to join'}
          </button>
          <button
            onClick={() => setActivePanel(p => (p === 'add' ? 'none' : 'add'))}
            className={activePanel === 'add' ? '' : 'btn-teal'}
            style={{
              width: 'auto', padding: '10px 18px',
              display: 'inline-flex', alignItems: 'center', gap: 7,
              ...(activePanel === 'add' ? { background: 'rgba(27,42,74,0.06)', border: 'none', borderRadius: 8, color: '#1B2A4A', cursor: 'pointer', font: '600 13px Inter, sans-serif' } : {}),
            }}
          >
            <Icon path={activePanel === 'add' ? ICONS.close : ICONS.plus} size={14} />
            {activePanel === 'add' ? 'Cancel' : 'Add a school'}
          </button>
        </div>
      </div>

      {activePanel === 'request' && <RequestToJoinPanel />}

      {activePanel === 'add' && (
        <div className='schools-form-enter' style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '24px 22px', marginTop: 18, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, font: '600 15px Inter, sans-serif', color: '#1B2A4A', marginBottom: 16 }}>
            <Icon path={ICONS.building} size={15} /> Add a private-label school
          </div>
          <SchoolForm
            initial={EMPTY_FORM}
            submitting={creating}
            submitLabel='Add school'
            onSubmit={async fields => {
              const result = await createSchool(fields)
              if (result.ok) setActivePanel('none')
              return result
            }}
          />
        </div>
      )}

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 6, marginTop: 24, marginBottom: 14, flexWrap: 'wrap' }}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '7px 14px', borderRadius: 20, border: 'none', cursor: 'pointer',
              font: '600 12.5px Inter, sans-serif',
              background: filter === f.key ? '#1B2A4A' : 'rgba(27,42,74,0.06)',
              color: filter === f.key ? '#fff' : 'rgba(27,42,74,0.6)',
              transition: 'background-color 0.15s, color 0.15s',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className='banner-error' role='alert' style={{ marginBottom: 14 }}>{error}</div>}

      {loading && <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>Loading…</div>}

      {!loading && filtered.length === 0 && (
        <div style={{ background: '#fff', border: '1px dashed rgba(27,42,74,0.15)', borderRadius: 14, padding: '40px 28px', textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, background: 'rgba(27,42,74,0.05)', color: 'rgba(27,42,74,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px',
          }}>
            <Icon path={ICONS.building} size={22} />
          </div>
          <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>No schools here</div>
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            {filter === 'registered' && "You're not an employee or contractor at any registered school yet — use Request to join above."}
            {filter === 'private' && 'Add a school you contract with to record their rate and attribute students at invite time.'}
            {filter === 'archived' && 'No archived private-label schools.'}
            {filter === 'all' && 'Add a private-label school, or request to join a registered one, using the buttons above.'}
          </div>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, overflow: 'hidden' }}>
          <div className='schools-grid-header' style={{
            display: 'grid', gridTemplateColumns: COLS, gap: 16, padding: '11px 18px',
            background: 'rgba(27,42,74,0.03)', borderBottom: '1px solid rgba(27,42,74,0.08)',
            font: '600 11px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', textTransform: 'uppercase', letterSpacing: '0.4px',
          }}>
            <HeaderCell label='School' sortKey='name' activeSort={sortKey} activeDir={sortDir} onSort={handleSort} />
            <span>Type</span>
            <span>Address</span>
            <span style={{ textAlign: 'right' }}>
              <HeaderCell label='Pay / hr' sortKey='rate' activeSort={sortKey} activeDir={sortDir} onSort={handleSort} />
            </span>
            <span>Email</span>
            <span>Phone</span>
            <span />
          </div>

          {filtered.map((row, i) => {
            const isLast = i === filtered.length - 1
            const isExpanded = expandedId === row.key
            const isEditing = editingId === row.key
            return (
              <div key={row.key} className='schools-row' style={{ borderBottom: isLast ? 'none' : '1px solid rgba(27,42,74,0.06)' }}>
                {isEditing && row.external ? (
                  <div style={{ padding: '18px' }}>
                    <SchoolForm
                      initial={{
                        name: row.external.name,
                        address: row.external.address ?? '',
                        contactName: row.external.contactName ?? '',
                        contactEmail: row.external.contactEmail ?? '',
                        contactPhone: row.external.contactPhone ?? '',
                        hourlyRate: row.external.hourlyRate === null ? '' : String(row.external.hourlyRate),
                      }}
                      submitting={updating === row.external.id}
                      submitLabel='Save changes'
                      onCancel={() => setEditingId(null)}
                      onSubmit={async fields => {
                        const result = await updateSchool(row.external!.id, fields)
                        if (result.ok) setEditingId(null)
                        return result
                      }}
                    />
                  </div>
                ) : (
                  <div className='schools-grid-row' style={{
                    display: 'grid', gridTemplateColumns: COLS, gap: 16, padding: '13px 18px',
                    alignItems: 'center', opacity: row.archived ? 0.6 : 1,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: TYPE_STYLE[row.type].bg, color: TYPE_STYLE[row.type].color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon path={row.type === 'private' ? ICONS.briefcase : ICONS.building} size={14} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ font: '600 13.5px Inter, sans-serif', color: '#1B2A4A', wordBreak: 'break-word' }}>{row.name}</div>
                        {row.archived && <div style={{ font: '700 10px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', textTransform: 'uppercase' }}>Archived</div>}
                      </div>
                    </div>
                    <div><Badge label={TYPE_LABEL[row.type]} tone={TYPE_TONE[row.type]} /></div>
                    <div style={{ font: '400 12.5px Inter, sans-serif', color: row.address ? 'rgba(27,42,74,0.6)' : 'rgba(27,42,74,0.3)', wordBreak: 'break-word' }}>
                      {row.address ?? '—'}
                    </div>
                    <div style={{ font: '500 13px Inter, sans-serif', color: row.rate !== null ? '#1B2A4A' : 'rgba(27,42,74,0.3)', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {formatRate(row.rate, row.rateCurrency)}
                    </div>
                    <div style={{ font: '400 12px Inter, sans-serif', color: row.email ? 'rgba(27,42,74,0.6)' : 'rgba(27,42,74,0.3)', wordBreak: 'break-word' }}>
                      {row.email ?? '—'}
                    </div>
                    <div style={{ font: '400 12px Inter, sans-serif', color: row.phone ? 'rgba(27,42,74,0.6)' : 'rgba(27,42,74,0.3)', wordBreak: 'break-word' }}>
                      {row.phone ?? '—'}
                    </div>
                    <div style={{ display: 'flex', gap: 2, justifySelf: 'end' }}>
                      <IconButton
                        icon={ICONS.chevronDown}
                        label={isExpanded ? 'Hide students' : 'Show students'}
                        onClick={() => setExpandedId(isExpanded ? null : row.key)}
                      />
                      {row.external && !row.archived && (
                        <>
                          <IconButton icon={ICONS.pencil} label='Edit' tone='teal' onClick={() => setEditingId(row.key)} />
                          <IconButton icon={ICONS.archive} label='Archive' tone='danger' onClick={() => archiveSchool(row.external!.id)} />
                        </>
                      )}
                      {row.external && row.archived && (
                        <IconButton icon={ICONS.rotate} label='Reactivate' tone='teal' onClick={() => reactivateSchool(row.external!.id)} />
                      )}
                    </div>
                  </div>
                )}
                {isExpanded && !isEditing && (
                  <div style={{ padding: '0 18px 16px 54px' }}>
                    <SchoolStudents schoolId={row.schoolId} isExternal={!!row.external} />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
